import { useState } from "react";
import { useT } from "../../i18n/hooks.ts";
import { TOOL_LABEL, type DrawingTool } from "../drawingTools.ts";
import { Dialog } from "../Dialog.tsx";

/**
 * Last step when drawing on the map (see SubBasinMap.tsx/App.tsx): the
 * geometry is already closed, it just needs a name — and, if it's a
 * sub-basin, confirming the area (pre-filled with `approximateAreaKm2`'s
 * approximation, editable because that's all it is, an approximation).
 */
export function NameDrawingDialog({
  tool,
  suggestedName,
  suggestedAreaKm2,
  onConfirm,
  onCancel,
}: {
  tool: DrawingTool;
  suggestedName: string;
  /** Only relevant for "subBasin" — undefined in the other two cases. */
  suggestedAreaKm2?: number;
  onConfirm: (name: string, areaKm2?: number) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [name, setName] = useState(suggestedName);
  const [area, setArea] = useState(String(suggestedAreaKm2 ?? 0));
  const isSubBasin = tool === "subBasin";

  function confirm() {
    if (!name.trim()) return;
    onConfirm(name.trim(), isSubBasin ? Number(area) || 0 : undefined);
  }

  return (
    <Dialog
      title={t("nameDrawingDialog.new", {
        tool: t(TOOL_LABEL[tool]),
      })}
      onClose={onCancel}
    >
      <div className="assigner-controls">
        <label>{t("common.name")}</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && confirm()}
        />
      </div>
      {isSubBasin && (
        <div className="assigner-controls">
          <label>{t("resultsPanel.areaKm2")}</label>
          <input type="number" step="0.01" value={area} onChange={(e) => setArea(e.target.value)} />
        </div>
      )}
      <div className="dialog-actions">
        <button className="button button-primary" disabled={!name.trim()} onClick={confirm}>
          {t("common.create")}
        </button>
        <button className="button" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
    </Dialog>
  );
}
